/**
 * Authenticated home hub (mobile UX batch A).
 *
 * Calm, Jobs-first landing instead of a long menu: the signed-in identity card, a Capture
 * Receipt action, a compact tile grid (Jobs / Estimates / Invoices / Review Queue), and a
 * single "More" row for everything else. Calendar + Priority Queue live in the global
 * header (HeaderIcons); nothing was removed, just relocated.
 */

import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useSession } from '../../../src/auth/session';
import { me } from '../../../src/api/client';
import { useQueueStore, selectPendingCount } from '../../../src/review/queueStore';
import { useConnectivity } from '../../../src/offline/useConnectivity';
import { SyncStatusBar } from '../../../src/offline/SyncStatusBar';
import { Screen, Text, Card, Button, ListRow } from '../../../src/components/ui';
import { BeamLogo } from '../../../src/components/brand/Logo';
import { HeaderIcons } from '../../../src/components/nav/HeaderIcons';
import { theme } from '../../../src/theme';

type Tile = { label: string; path: string; primary?: boolean; badge?: number };

function HubTile({
  label,
  badge,
  primary,
  onPress,
}: {
  label: string;
  badge?: number;
  primary?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed }) => [styles.tile, primary && styles.tilePrimary, pressed && styles.tilePressed]}
    >
      <Text variant="bodyStrong" color={primary ? 'onPrimary' : 'primary'}>
        {label}
      </Text>
      {badge && badge > 0 ? (
        <Text variant="small" color={primary ? 'onPrimary' : 'secondary'}>
          {badge} pending
        </Text>
      ) : null}
    </Pressable>
  );
}

export default function HomeScreen() {
  const logout = useSession((s) => s.logout);
  const sessionUser = useSession((s) => s.user);
  const router = useRouter();
  const pendingCount = useQueueStore(selectPendingCount);
  const connectivity = useConnectivity();

  const { data: meData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: me,
    initialData: sessionUser ?? undefined,
    staleTime: 60_000,
  });

  const tiles: Tile[] = [
    { label: 'Jobs', path: '/(app)/jobs', primary: true },
    { label: 'Estimates', path: '/(app)/estimates' },
    { label: 'Invoices', path: '/(app)/invoices' },
    { label: 'Review Queue', path: '/(app)/review', badge: pendingCount },
  ];

  return (
    <Screen scroll padded={false}>
      <SyncStatusBar {...connectivity} />
      <View style={styles.body}>
        <View style={styles.headerRow}>
          <BeamLogo size={30} />
          <HeaderIcons routeName="home" />
        </View>

        <Text variant="label" color="secondary">
          Signed in as
        </Text>

        {isLoading && !meData ? (
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading user details" />
        ) : isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {error instanceof Error ? error.message : 'Failed to load user details.'}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => refetch()} />
          </Card>
        ) : meData ? (
          <Card>
            <Text variant="title">{meData.fullName}</Text>
            <Text variant="body" color="secondary">
              {meData.email}
            </Text>
            <Text variant="label" color="link">
              {meData.role}
            </Text>
            <Text variant="data" color="secondary">
              Org {meData.organizationId}
            </Text>
          </Card>
        ) : null}

        <View style={styles.createSection}>
          <Text variant="label" color="secondary">
            Create
          </Text>
          <ListRow
            title="New estimate"
            subtitle="Draft a priced estimate"
            onPress={() => router.push('/(app)/estimates/new' as never)}
            accessibilityLabel="New estimate"
          />
          <ListRow
            title="New service call"
            subtitle="Schedule a job for a customer"
            onPress={() => router.push('/(app)/schedule/new?type=service_call' as never)}
            accessibilityLabel="New service call"
          />
          <ListRow
            title="New invoice"
            subtitle="Bill an estimate or a job"
            onPress={() => router.push('/(app)/invoices/new' as never)}
            accessibilityLabel="New invoice"
          />
          <ListRow
            title="New customer"
            subtitle="Add a customer or contact"
            onPress={() => router.push('/(app)/customers' as never)}
            accessibilityLabel="New customer"
          />
        </View>

        <Button title="Capture Receipt" onPress={() => router.push('/(app)/capture')} accessibilityLabel="Capture a receipt" />

        <View style={styles.grid}>
          {tiles.map((t) => (
            <HubTile
              key={t.path}
              label={t.label}
              badge={t.badge}
              primary={t.primary}
              onPress={() => router.push(t.path as never)}
            />
          ))}
        </View>

        <ListRow
          title="More"
          subtitle="Customers, messages, tasks, permits, reports, assistant, help"
          onPress={() => router.push('/(app)/more')}
          accessibilityLabel="More features"
        />

        <Button title="Sign out" variant="secondary" onPress={logout} accessibilityLabel="Sign out" />
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  body: { paddingHorizontal: theme.layout.screenPadding, gap: theme.spacing.lg, alignItems: 'stretch' },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  createSection: { gap: theme.spacing.sm },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
  tile: {
    flexBasis: '47%',
    flexGrow: 1,
    borderRadius: theme.radius.md,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    backgroundColor: theme.color.bg.surface,
    paddingVertical: theme.spacing.xl,
    paddingHorizontal: theme.spacing.lg,
    gap: theme.spacing.xs,
  },
  tilePrimary: { backgroundColor: theme.color.action.primary, borderColor: theme.color.action.primary },
  tilePressed: { opacity: 0.85 },
});
